import { Download, Pin, Trash, Upload } from "lucide-react";
import { useMemo, useState } from "react";
import { nowIso } from "../constants";
import { clearStore, putRecord } from "../storage/db";
import { createBackupZip, importBackupZip } from "../storage/zipBackup";
import type { MemoryItem } from "../types/app";
import { createId } from "../utils/id";

interface Props {
  memories: MemoryItem[];
  onSave: (memory: MemoryItem) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onRefresh: () => Promise<void>;
  onError: (message: string) => void;
}

export function MemoryPage({ memories, onSave, onDelete, onRefresh, onError }: Props) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => memories.filter((item) => item.content.includes(query) || item.category.includes(query)), [memories, query]);
  return (
    <main className="screen">
      <h1>記憶管理</h1>
      <section className="panel">
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="検索" />
        <div className="button-row">
          <button type="button" onClick={() => downloadJson("memories.json", memories)}><Download /> JSONエクスポート</button>
          <label className="file-button"><Upload /> JSONインポート<input hidden type="file" accept="application/json" onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            const items = JSON.parse(await file.text()) as MemoryItem[];
            for (const item of items) await putRecord("memories", { ...item, id: item.id || createId("memory"), updatedAt: nowIso() });
            await onRefresh();
          }} /></label>
          <button type="button" onClick={async () => { const blob = await createBackupZip(); downloadBlob("voice-ai-backup.zip", blob); }}><Download /> 完全バックアップZIP</button>
          <label className="file-button"><Upload /> ZIP復元<input hidden type="file" accept="application/zip,.zip" onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            try {
              await importBackupZip(file);
              await onRefresh();
            } catch (error) {
              onError(error instanceof Error ? error.message : "バックアップ復元に失敗しました。");
            }
          }} /></label>
          <button type="button" onClick={async () => { if (confirm("記憶を全削除しますか？")) { await clearStore("memories"); await onRefresh(); } }}>全削除</button>
        </div>
      </section>
      {filtered.map((memory) => (
        <article className="memory-card" key={memory.id}>
          <textarea value={memory.content} onChange={(event) => void onSave({ ...memory, content: event.target.value, updatedAt: nowIso() })} />
          <select value={memory.category} onChange={(event) => void onSave({ ...memory, category: event.target.value as MemoryItem["category"], updatedAt: nowIso() })}>
            <option value="pinned">固定記憶</option>
            <option value="normal">通常記憶</option>
            <option value="temporary">一時記憶</option>
          </select>
          <button type="button" onClick={() => void onSave({ ...memory, isPinned: !memory.isPinned, category: memory.isPinned ? "normal" : "pinned", updatedAt: nowIso() })}><Pin /> ピン</button>
          <button type="button" onClick={() => void onDelete(memory.id)}><Trash /> 削除</button>
        </article>
      ))}
    </main>
  );
}

function downloadJson(filename: string, data: unknown): void {
  downloadBlob(filename, new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
